/**
 * Dream Memory™ — Central Intelligence Layer for AIMakerStudio
 *
 * Dream Memory™ is the single source of truth that powers every module:
 * Dream DNA Studio™, Dream Coach™, DreamSite™, Dream Launch Navigator™,
 * Visual Companion™, Dream Journal™, Portfolio, PDF Export, Marketplace,
 * and all future integrations.
 *
 * It remembers the user like a trusted creative partner and never asks
 * the same question twice.
 */

// ─── Core Identity ────────────────────────────────────────────────────────────

export interface DreamMemoryIdentity {
  businessName?: string;
  businessType?: string;
  industry?: string;
  niche?: string;
  preferredLanguage?: string;
  preferredPlatform?: string;
}

// ─── Audience & Persona ───────────────────────────────────────────────────────

export interface DreamMemoryAudience {
  targetAudience?: string;
  secondaryAudience?: string;
  customerPersona?: string;
  painPoints?: string[];
  desires?: string[];
}

// ─── Brand ────────────────────────────────────────────────────────────────────

export interface DreamMemoryBrand {
  brandPersonality?: string[];
  brandArchetype?: string;
  tone?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    mood?: string;
  };
  typography?: {
    headline?: string;
    body?: string;
    accent?: string;
  };
  preferredVisualStyle?: string;
  imageStyle?: string;
}

// ─── Products & Services ──────────────────────────────────────────────────────

export interface DreamMemoryProducts {
  products?: { name: string; description: string; priceRange?: string }[];
  services?: { name: string; description: string; priceRange?: string }[];
  pricingStrategy?: string;
  pricingLevel?: 'budget' | 'mid-range' | 'premium' | 'luxury';
  monetizationMethods?: string[];
}

// ─── Strategy ─────────────────────────────────────────────────────────────────

export interface DreamMemoryStrategy {
  marketingStrategy?: string;
  marketingChannels?: string[];
  contentStrategy?: string;
  launchPlan?: string;
  launchPhases?: {
    phase1?: string;
    phase2?: string;
    phase3?: string;
    quickWins?: string[];
  };
}

// ─── Visuals ─────────────────────────────────────────────────────────────────

export interface DreamMemoryVisual {
  id: string;
  b64_json?: string;
  url?: string;
  label: string;
  sectionId: string;
  prompt?: string;
  style?: string;
  isFavorite: boolean;
  createdAt: number;
}

// ─── Project Intelligence ─────────────────────────────────────────────────────

export interface DreamMemoryProject {
  projectGoals?: string[];
  projectMilestones?: { label: string; done: boolean; dueDate?: string }[];
  projectStatus?: 'draft' | 'in-progress' | 'launched' | 'paused';
  dreamScore?: number;
  dreamScoreDimensions?: Record<string, number>;
}

// ─── User Edits & Overrides ───────────────────────────────────────────────────

export interface DreamMemoryEdits {
  /** Fields the user has explicitly edited — these are never overwritten by AI regeneration */
  userEditedFields: Set<string>;
  /** Timestamp of last user edit per field */
  lastEditedAt: Record<string, string>;
}

// ─── Generated Sections ───────────────────────────────────────────────────────

export interface DreamMemorySections {
  /** Stores the generated content for each section by sectionId */
  generatedContent: Record<string, string>;
  /** Tracks which sections have been generated */
  generatedSections: Set<string>;
  /** Tracks which sections have been saved */
  savedSections: Set<string>;
  /** Version history per section (last 5 versions) */
  sectionVersions: Record<string, string[]>;
}

// ─── Main Dream Memory™ Object ────────────────────────────────────────────────

export interface DreamMemory {
  // Core dream text
  dreamText: string;

  // Identity
  identity: DreamMemoryIdentity;

  // Audience
  audience: DreamMemoryAudience;

  // Brand
  brand: DreamMemoryBrand;

  // Products & Services
  products: DreamMemoryProducts;

  // Strategy
  strategy: DreamMemoryStrategy;

  // Visuals
  generatedVisuals: DreamMemoryVisual[];
  favoriteVisuals: string[]; // visual IDs

  // Project
  project: DreamMemoryProject;

  // Sections
  sections: DreamMemorySections;

  // Edits tracking
  edits: DreamMemoryEdits;

  // Meta
  createdAt: string;
  updatedAt: string;
  projectId?: string;
}

// ─── Change Detection ─────────────────────────────────────────────────────────

/** Fields that, when changed, should trigger smart refresh prompts */
export const SMART_REFRESH_TRIGGERS: Record<string, {
  label: string;
  relatedSections: string[];
  relatedFields: string[];
  promptKey: string;
}> = {
  businessName: {
    label: 'Business Name',
    relatedSections: ['brandKit', 'brand', 'dreamSummary', 'vision'],
    relatedFields: ['brand.tone', 'brand.brandPersonality'],
    promptKey: 'businessName',
  },
  targetAudience: {
    label: 'Target Audience',
    relatedSections: ['idealCustomer', 'contentStrategy', 'content', 'launchStrategy', 'launchPlan', 'businessModel'],
    relatedFields: ['audience.customerPersona', 'strategy.marketingStrategy'],
    promptKey: 'targetAudience',
  },
  brandColors: {
    label: 'Brand Colors',
    relatedSections: ['brandKit', 'brand'],
    relatedFields: ['brand.preferredVisualStyle'],
    promptKey: 'brandColors',
  },
  businessType: {
    label: 'Business Type',
    relatedSections: ['businessModel', 'products', 'launchStrategy', 'launchPlan', 'roadmap90', 'aiStack'],
    relatedFields: ['products.pricingStrategy', 'strategy.marketingStrategy'],
    promptKey: 'businessType',
  },
  dreamText: {
    label: 'Dream',
    relatedSections: ['dreamSummary', 'vision', 'dreamScore', 'businessOpportunities'],
    relatedFields: [],
    promptKey: 'dreamText',
  },
  pricingStrategy: {
    label: 'Pricing Strategy',
    relatedSections: ['products', 'businessModel', 'launchStrategy'],
    relatedFields: ['products.pricingLevel'],
    promptKey: 'pricingStrategy',
  },
  marketingStrategy: {
    label: 'Marketing Strategy',
    relatedSections: ['contentStrategy', 'content', 'launchStrategy', 'launchPlan'],
    relatedFields: ['strategy.marketingChannels'],
    promptKey: 'marketingStrategy',
  },
};

// ─── Context Value ────────────────────────────────────────────────────────────

export interface DreamMemoryContextValue {
  memory: DreamMemory | null;
  isInitialized: boolean;

  /** Initialize Dream Memory™ for a project */
  initMemory: (dreamText: string, projectId?: string) => void;

  /** Update a specific field in Dream Memory™ */
  updateMemory: <K extends keyof DreamMemory>(
    field: K,
    value: DreamMemory[K],
    isUserEdit?: boolean
  ) => void;

  /** Update nested identity/audience/brand/products/strategy fields */
  updateMemoryField: (path: string, value: any, isUserEdit?: boolean) => void;

  /** Sync Dream Memory™ from a BusinessProfile */
  syncFromBusinessProfile: (profile: import('@/lib/businessProfile/types').BusinessProfile) => void;

  /** Record a generated section */
  recordGeneratedSection: (sectionId: string, content: string) => void;

  /** Record a saved section */
  recordSavedSection: (sectionId: string) => void;

  /** Add a generated visual */
  addGeneratedVisual: (visual: DreamMemoryVisual) => void;

  /** Toggle a visual as favorite */
  toggleFavoriteVisual: (visualId: string) => void;

  /** Check if a field has been user-edited */
  isUserEdited: (fieldPath: string) => boolean;

  /** Get a smart context string for AI prompts */
  getAIContext: () => string;

  /** Check if a section has been generated */
  isSectionGenerated: (sectionId: string) => boolean;

  /** Get stored content for a section */
  getSectionContent: (sectionId: string) => string | null;

  /** Smart refresh prompt state */
  pendingRefreshPrompt: SmartRefreshPrompt | null;
  dismissRefreshPrompt: () => void;
  acceptRefreshPrompt: () => void;

  /** Load Dream Memory™ from persisted data */
  loadMemory: (data: Partial<DreamMemory>) => void;

  /** Serialize Dream Memory™ for persistence */
  serializeMemory: () => object;

  /** Clear Dream Memory™ */
  clearMemory: () => void;
}

// ─── Smart Refresh Prompt ─────────────────────────────────────────────────────

export interface SmartRefreshPrompt {
  changedField: string;
  changedFieldLabel: string;
  relatedSections: string[];
  message: string;
  onAccept?: () => void;
}
